import { FormEvent, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { krakenAPI, krakenError, krakenErrorCode } from '../services/krakenApi'
import type { KrakenConnection } from '../types/kraken'
import Spinner from '../components/Spinner'
import ConfirmDialog from '../components/ConfirmDialog'

const NEEDED = [
  'Funds permissions — Query',
  'Orders and trades — Query open orders & trades',
  'Orders and trades — Query closed orders & trades',
  'Orders and trades — Create & modify orders',
  'Orders and trades — Cancel & close orders',
]

const FORBIDDEN = ['Funds permissions — Withdraw', 'Add withdrawal addresses']

/**
 * Connect a Kraken API key. The key and secret are sent once, stored
 * encrypted, and never shown again. A key that can withdraw is refused and not
 * stored: FinCreator must never hold a key that can move funds out.
 */
const Exchange = () => {
  const [conn, setConn] = useState<KrakenConnection | null>(null)
  const [allowed, setAllowed] = useState<boolean | null>(null)
  const [apiKey, setApiKey] = useState('')
  const [apiSecret, setApiSecret] = useState('')
  const [attested, setAttested] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [withdrawWarning, setWithdrawWarning] = useState<string | null>(null)
  const [confirmDisconnect, setConfirmDisconnect] = useState(false)

  const load = async () => {
    try {
      const a = await krakenAPI.access()
      setAllowed(a.allowed)
      if (a.allowed) setConn(await krakenAPI.connection())
    } catch {
      setAllowed(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const save = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    setWithdrawWarning(null)
    try {
      await krakenAPI.connect({ api_key: apiKey, api_secret: apiSecret, confirm_no_withdrawal_permissions: attested })
      setApiKey('')
      setApiSecret('')
      setAttested(false)
      setConn(await krakenAPI.connection())
    } catch (err) {
      const message = krakenError(err, 'Could not connect Kraken.')
      if (krakenErrorCode(err) === 'key_can_withdraw') setWithdrawWarning(message)
      else setError(message)
    } finally {
      // The secret never lingers in memory longer than the request.
      setApiSecret('')
      setSaving(false)
    }
  }

  const disconnect = async () => {
    try {
      await krakenAPI.disconnect(true)
      setConfirmDisconnect(false)
      setConn(await krakenAPI.connection())
    } catch (err) {
      setError(krakenError(err, 'Could not disconnect.'))
      setConfirmDisconnect(false)
    }
  }

  if (allowed === null) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    )
  }
  if (!allowed) {
    return (
      <div className="rounded-lg border border-terminal-border bg-terminal-surface p-8 text-center text-gray-300">
        Exchange connections are restricted to FinCreator admins.
      </div>
    )
  }

  const input =
    'mt-1 w-full rounded-md border border-terminal-border bg-terminal-bg px-3 py-2 font-mono text-sm text-gray-50 focus:border-primary-500 focus:outline-none'

  return (
    <div className="mx-auto max-w-2xl space-y-6 text-gray-100">
      <div>
        <h1 className="text-3xl font-bold text-gray-50">Connections</h1>
        <p className="mt-1 text-sm text-gray-400">Connect Kraken for confirm-first live trading.</p>
      </div>

      <section className="rounded-lg border border-terminal-border bg-terminal-surface p-4 md:p-6">
        <h2 className="text-xs font-medium uppercase tracking-wider text-gray-400">Kraken</h2>
        {conn?.connected ? (
          <div className="mt-2 space-y-2">
            <p className="text-lg font-bold text-green-300" data-testid="kraken-connected">
              ✓ Kraken connected
            </p>
            <p className="text-sm text-gray-400">
              Key …{conn.key_hint} · verified by a live Balance call · cannot withdraw
              {conn.verified_at ? ` · saved ${new Date(conn.verified_at).toLocaleString()}` : ''}
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Link to="/trades" className="rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700">
                Go to Trades
              </Link>
              <button
                type="button"
                onClick={() => setConfirmDisconnect(true)}
                className="rounded-md border border-terminal-border px-4 py-2 text-sm text-gray-300 hover:bg-white/5"
              >
                Disconnect
              </button>
            </div>
          </div>
        ) : (
          <p className="mt-2 text-sm text-gray-300">
            {conn?.error ? <span className="text-red-300">{conn.error}</span> : 'Not connected.'}
          </p>
        )}
      </section>

      {withdrawWarning && (
        <div className="rounded-lg border-2 border-red-500 bg-red-500/15 p-4" role="alert" data-testid="withdraw-warning">
          <p className="font-bold text-red-200">{withdrawWarning}</p>
          <p className="mt-1 text-sm text-red-100">The key was not saved. On Kraken, create a new key with withdrawals off and paste that one.</p>
        </div>
      )}

      <section className="rounded-lg border border-terminal-border bg-terminal-surface p-4 md:p-6">
        <h2 className="text-xs font-medium uppercase tracking-wider text-gray-400">
          {conn?.connected ? 'Replace key' : 'Connect a key'}
        </h2>
        <div className="mt-3 grid gap-4 text-sm sm:grid-cols-2">
          <div>
            <p className="font-semibold text-green-300">Turn on</p>
            <ul className="mt-1 space-y-0.5 text-gray-300">
              {NEEDED.map((p) => (
                <li key={p}>✓ {p}</li>
              ))}
            </ul>
          </div>
          <div>
            <p className="font-semibold text-red-300">Leave off</p>
            <ul className="mt-1 space-y-0.5 text-gray-300">
              {FORBIDDEN.map((p) => (
                <li key={p}>✗ {p}</li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-gray-500">
              Recommended: set a nonce window (e.g. 10000) on the key — FinCreator's API and worker both use it.
            </p>
          </div>
        </div>

        <form onSubmit={save} className="mt-5 space-y-3" autoComplete="off">
          <label className="block text-xs text-gray-400">
            API key
            <input className={input} value={apiKey} onChange={(e) => setApiKey(e.target.value)} autoComplete="off" spellCheck={false} required />
          </label>
          <label className="block text-xs text-gray-400">
            Private key (secret)
            <input
              className={input}
              type="password"
              value={apiSecret}
              onChange={(e) => setApiSecret(e.target.value)}
              autoComplete="new-password"
              spellCheck={false}
              required
            />
          </label>
          <label className="flex items-start gap-2 text-sm text-gray-300">
            <input type="checkbox" checked={attested} onChange={(e) => setAttested(e.target.checked)} className="mt-1" />
            <span>
              This key has neither <strong>Withdraw funds</strong> nor <strong>Add withdrawal addresses</strong> enabled.
              <span className="block text-xs text-gray-500">
                FinCreator checks the withdraw permission with Kraken and refuses keys that have it. Kraken offers no way
                to check the add-addresses permission, so that one relies on you.
              </span>
            </span>
          </label>
          {error && (
            <p className="rounded-md border border-red-500/40 bg-red-500/10 p-2 text-sm text-red-200" role="alert">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={saving || !attested || !apiKey || !apiSecret}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-primary-600 px-4 py-2.5 font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving && <Spinner size="sm" className="border-white border-t-transparent" />}
            {saving ? 'Checking the key with Kraken…' : 'Save and verify'}
          </button>
          <p className="text-xs text-gray-500">
            Stored encrypted. Never shown again, never logged, never sent back to this browser.
          </p>
        </form>
      </section>

      <ConfirmDialog
        isOpen={confirmDisconnect}
        title="Disconnect Kraken?"
        message="The stored key is deleted. Orders already on Kraken stay there, but FinCreator stops tracking them until you reconnect."
        confirmLabel="Disconnect"
        danger
        onConfirm={disconnect}
        onCancel={() => setConfirmDisconnect(false)}
      />
    </div>
  )
}

export default Exchange
